import {Trans} from '@lingui/macro';
import produce from 'immer';
import {flatMap, pickBy} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {isDefined, OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {WithClientId} from '../../../editors/commons/clientIdModel';
import {EnumId, Parameter, StringParameter} from '../../../editors/commons/parameterModel';
import {isStringTemplate, StringOrTemplate} from '../../../editors/commons/stringTemplateModel';
import {Expression} from '../../../editors/expressions/expressionModel';
import {isExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {getCompatibleInferredTypesWithManifest} from '../../../editors/expressions/expressionResultHelpers';
import {TeammateListResult, TeammateResult} from '../../../editors/expressions/infer/expressionResultModel';
import {useRuleEditorContext} from '../../../editors/ruleEditor/ruleEditorContext';
import {
  ANDConditions,
  CommentBodyAndAuthorsCondition,
  isCommentBodyAndAuthorsRuleCondition,
  RuleConditionTypesEnum,
} from '../../ruleConditionModel';
import {TriggerWithConditions} from '../../ruleTriggerHelpers';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {useOnSelectCreateExpressionRef} from '../hooks/useOnSelectCreateExpression';
import {InputStringTemplateParameter} from '../inputs/inputStringTemplateParameter';
import {EnumParameter} from '../parameters/enumParameter';
import {TeammateSelectedDropdownItem, TeammatesParameter} from '../parameters/teammateParameter';
import {
  MultilineRuleParameterCardDefaultTitleStyled,
  RuleMultilineParameterCard,
} from '../ruleMultilineParameterCard';
import {RuleTriggerIcon} from '../ruleTriggerIcon';

const {isEnum} = Parameter;

/*
 * Types.
 */

interface RuleCommentAddedTriggerCardProps {
  triggerWithConditions: TriggerWithConditions;
  isDisabled?: boolean;
  expressions: ReadonlyArray<Expression>;
  errorMessage?: ReactNode;

  onUpdateConditions: HandlerOf<ANDConditions>;
  onDelete?: Handler;
}

/*
 * Styles.
 */

const StyledSecondLineWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  grid-template-columns: auto auto 1fr;
  grid-column-gap: 5px;
  align-items: center;
`;

/*
 * Component.
 */

export const RuleCommentAddedTriggerCard: FC<RuleCommentAddedTriggerCardProps> = (props) => {
  const {triggerWithConditions, expressions, errorMessage, isDisabled, onUpdateConditions, onDelete} = props;
  const {trigger, conditions} = triggerWithConditions;
  const {ruleIntegration} = useRuleEditorContext();

  // Hardcoded condition as conditions in the card are optional
  const commentBodyConditionDefinition = ruleIntegration.findConditionDefinition({
    integration_id: 0,
    type: RuleConditionTypesEnum.COMMENT_BODY_AND_AUTHORS,
  });

  const publicParameters = commentBodyConditionDefinition
    ? pickBy(commentBodyConditionDefinition.parameters, Parameter.isPublic)
    : undefined;

  const comparatorParameter = commentBodyConditionDefinition?.resolvedParameters.comparator;

  const defaultComparator = comparatorParameter ? String(comparatorParameter?.default) : 'contains';

  const [selectedComparator] = flatMap(conditions, (orConditions) =>
    flatMap(
      orConditions.filter(isCommentBodyAndAuthorsRuleCondition),
      (condition) => condition.params.comparator,
    ),
  ).filter(isDefined);

  const condition = getCondition(conditions);
  const commentBodyParameter = publicParameters?.text as StringParameter;
  const commentBodyText = condition?.params?.text;
  const selectedTeammates = condition?.params.teammates ?? [];

  const onCommentBodyChange = (value: StringOrTemplate) => {
    onUpdateConditions(
      produce(conditions, (draft) => {
        const draftCondition = getCondition(draft);

        const isValueEmpty = !isStringTemplate(value) && value.trim() === '';
        const currentComparator = draftCondition?.params.comparator;

        if (!draftCondition) {
          return;
        }

        if (isValueEmpty) {
          draftCondition.params.comparator = undefined;
          draftCondition.params.text = undefined;
          return;
        }

        draftCondition.params.comparator = currentComparator ?? defaultComparator;
        draftCondition.params.text = value;
      }),
    );
  };

  const onSelectCreateBodyExpression = useOnSelectCreateExpressionRef((expressionRef) => {
    if (!commentBodyText) {
      onCommentBodyChange({template: [expressionRef]});
    } else if (isStringTemplate(commentBodyText)) {
      onCommentBodyChange({template: [...commentBodyText.template, expressionRef]});
    } else {
      onCommentBodyChange({template: [commentBodyText, expressionRef]});
    }
  }, getCompatibleInferredTypesWithManifest(commentBodyParameter));

  const onCommentComparatorChange = (updatedComparator?: OrNull<SelectedDropdownItem<{id: EnumId}>>) => {
    onUpdateConditions(
      produce(conditions, (draft) => {
        const draftCondition = getCommentBodyAndAuthorsCondition(draft);

        if (!draftCondition) {
          return;
        }

        if (isExpressionRef(updatedComparator)) {
          return;
        }

        if (!updatedComparator) {
          draftCondition.params.text = undefined;
        }

        draftCondition.params.comparator = updatedComparator ?? undefined;
      }),
    );
  };

  const maybeRenderComparatorPicker = () => {
    if (!comparatorParameter || !isEnum(comparatorParameter)) {
      return null;
    }

    return (
      <EnumParameter
        enumParameter={comparatorParameter}
        selectedItem={selectedComparator}
        isDisabled={isDisabled}
        onChange={onCommentComparatorChange}
      />
    );
  };

  const onTeammatesChange = (updatedTeammateIds: OrNull<ReadonlyArray<TeammateSelectedDropdownItem>>) => {
    const updatedConditions = produce(conditions, (draft) => {
      const draftCondition = getCommentBodyAndAuthorsCondition(draft);

      if (!draftCondition) {
        return;
      }

      draftCondition.params.teammates = updatedTeammateIds ?? [];
    });

    onUpdateConditions(updatedConditions);
  };

  const onSelectCreateTeammateExpression = useOnSelectCreateExpressionRef(
    (expressionRef) => onTeammatesChange([...(condition?.params.teammates ?? []), expressionRef]),
    [TeammateResult, TeammateListResult],
  );

  // This should not happen, but safety check.
  if (!commentBodyConditionDefinition) {
    return (
      <RuleMultilineParameterCard
        icon={<RuleTriggerIcon trigger={trigger} />}
        title={<Trans id="trigger-manifest-not-found">{trigger} not found</Trans>}
        onCancel={onDelete}
      />
    );
  }

  const title = (
    <>
      <MultilineRuleParameterCardDefaultTitleStyled>
        <Trans id="rule-editor-trigger-comment-added-title">New comment is added from</Trans>
      </MultilineRuleParameterCardDefaultTitleStyled>
      <TeammatesParameter
        selectedItems={selectedTeammates}
        expressions={expressions}
        isDisabled={isDisabled}
        onChange={onTeammatesChange}
        canSelectAnyItem
        onSelectCreateExpression={onSelectCreateTeammateExpression}
      />
    </>
  );

  return (
    <RuleMultilineParameterCard
      title={title}
      icon={<RuleTriggerIcon trigger={trigger} />}
      errorMessage={errorMessage}
      onCancel={onDelete}
    >
      <StyledSecondLineWrapper>
        <MultilineRuleParameterCardDefaultTitleStyled>
          <Trans id="rule-editor-trigger-comment-added-body">Body</Trans>
        </MultilineRuleParameterCardDefaultTitleStyled>
        {maybeRenderComparatorPicker()}
        <InputStringTemplateParameter
          stringParameter={commentBodyParameter}
          expressions={expressions}
          value={commentBodyText}
          onValueChange={onCommentBodyChange}
          isDisabled={isDisabled}
          onSelectCreateExpression={onSelectCreateBodyExpression}
        />
      </StyledSecondLineWrapper>
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function getCommentBodyAndAuthorsCondition(
  conditions: ANDConditions,
): WithClientId<CommentBodyAndAuthorsCondition> | undefined {
  return flatMap(conditions).find(isCommentBodyAndAuthorsRuleCondition);
}

function getCondition(conditions: ANDConditions): WithClientId<CommentBodyAndAuthorsCondition> | undefined {
  return flatMap(conditions, (orConditions) => orConditions).find(isCommentBodyAndAuthorsRuleCondition);
}
